/**
 * OsintSourcesCard — external-source citations for an OSINT report, rendered
 * as interactive "exhibits" in the same court-exhibit style as the
 * deep-research EvidenceCard (S# ordinal · text · source chip · open arrow),
 * but for external URLs instead of graph doc:/node: refs. Clicking a row opens
 * the source in a new tab; [S#] citations in the report scroll to and flash
 * the matching row.
 */

import { ArrowUpRight, Globe } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { RelativeTime } from '@/components/custom-ui/RelativeTime'
import { Section } from '@/components/search/detail/Section'
import type { OsintSource } from '@/services/api/osint'
import { cn } from '@/utils/cn'
import { hostOf } from '@/utils/url'

export function OsintSourcesCard({
  sources,
  highlightIndex,
}: {
  sources: OsintSource[]
  /** 0-based index of the row to scroll to and flash (report [S#] click). */
  highlightIndex?: number | null
}) {
  const { t } = useTranslation()
  const rowRefs = useRef<(HTMLLIElement | null)[]>([])

  useEffect(() => {
    if (highlightIndex == null) return
    rowRefs.current[highlightIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightIndex])

  if (sources.length === 0) return null

  return (
    <Section title={t('osint.sources')} hint={t('osint.sourcesHint', { count: sources.length })}>
      <ol className="flex flex-col gap-2">
        {sources.map((s, i) => {
          const url = s.source_url
          const host = hostOf(url)
          return (
            <li
              key={url ?? `osint-source-${i}`}
              ref={el => {
                rowRefs.current[i] = el
              }}
            >
              <a
                href={url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!url}
                className={cn(
                  'group relative grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-md border bg-muted/20 px-3 py-2.5 text-left transition-all duration-150',
                  url
                    ? 'cursor-pointer border-border/50 hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
                    : 'pointer-events-none cursor-default border-border/30',
                  highlightIndex === i && 'border-primary bg-primary/10 ring-2 ring-primary'
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-0.5 inline-flex size-7 flex-none items-center justify-center rounded-sm border bg-background font-mono text-3xs font-medium tabular-nums transition-colors',
                    url
                      ? 'border-border/60 text-muted-foreground group-hover:border-primary/40 group-hover:text-foreground'
                      : 'border-border/40 text-muted-foreground/60'
                  )}
                >
                  S{i + 1}
                </span>

                <div className="flex min-w-0 flex-col gap-2">
                  <span className="text-sm leading-relaxed text-foreground">
                    {s.text || url || t('osint.sourceUntitled')}
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    {host && (
                      <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider text-primary">
                        <Globe className="size-3" />
                        <span className="font-mono text-4xs normal-case tracking-normal text-muted-foreground/80">
                          {host}
                        </span>
                      </span>
                    )}
                    {s.retrieved_at && (
                      <span className="text-3xs text-muted-foreground/70">
                        {t('osint.retrieved')} <RelativeTime at={s.retrieved_at} />
                      </span>
                    )}
                  </div>
                </div>

                {url ? (
                  <ArrowUpRight
                    aria-hidden
                    className="mt-1 size-4 flex-none text-muted-foreground/40 transition-all duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
                  />
                ) : (
                  <span className="mt-1 size-4 flex-none" aria-hidden />
                )}
              </a>
            </li>
          )
        })}
      </ol>
    </Section>
  )
}
